import './EditVenue.scss';

import { useEffect, useMemo, useState } from 'react';
import { FiCheck, FiX } from 'react-icons/fi';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import chevronLeft from '../../assets/images/icons/chevron-left.svg';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import VenueTempLogin from '../../components/VenueTempLogin/VenueTempLogin';
import VenueInformation from '../../components/VenueInformation/VenueInformation';
import api from '../../services/api';
import Button from '../../components/Buttons/Button';
import VenueFeeFields from '../../components/VenueFeeFields/VenueFeeFields';
import VenueFloorPlan from '../../components/VenueFloorPlan/VenueFloorPlan';
import VenueInviteLink from '../../components/VenueInviteLink/VenueInviteLink';

const EditVenue = () => {
  const navigate = useNavigate();
  const { venueId } = useParams();
  const [searchParams, setUrlSearch] = useSearchParams();
  const moderatingVenue = useMemo(() => {
    return !!searchParams.get("moderating");
  }, [searchParams]);
  const [specVenue, setSpecVenue] = useState({});
  const [clickCount, setClickCount] = useState(0);
  useEffect(() => {
    const getVenue = async () => {
      try {
        // Admin-only read: a stale token 401s (refreshed) instead of coming back stripped.
        const response = await api.get(`/venues/${venueId}/admin`);
        setSpecVenue(response.data);
      } catch (err) {
        console.log(
          `error retreiving specific venue. this was the error: ${err}`
        );
      }
    };
    getVenue();
  }, [venueId]);

  // Open / Hidden / Closed. `status` drives whether the venue shows in the
  // app (active vs inactive); `closed` distinguishes permanently-shut from
  // just-hidden among the inactive ones.
  const venueLifecycle = specVenue.status === 'active'
    ? 'open'
    : (specVenue.closed ? 'closed' : 'hidden');

  const setLifecycle = async (target) => {
    if (target === venueLifecycle) return;
    const map = {
      open: { status: 'active', closed: false, confirm: null },
      hidden: { status: 'inactive', closed: false, confirm: 'Hide this venue? It stays a real venue but will no longer show in the app.' },
      closed: { status: 'inactive', closed: true, confirm: 'Mark this venue permanently closed? It will be removed from the app.' },
    };
    const cfg = map[target];
    if (cfg.confirm && !window.confirm(cfg.confirm)) return;
    try {
      await api.put(`/venues/${venueId}`, { status: cfg.status, closed: cfg.closed });
      setSpecVenue((prev) => ({ ...prev, status: cfg.status, closed: cfg.closed }));
    } catch (err) {
      console.log(`error setting venue lifecycle: ${err}`);
    }
  };

  const handleVenueActivation = async () => {
    try {
      if (clickCount === 1) {
        // Pending-review venues (from venue-sync) + inactive venues both get
        // activated via PUT. Anything else gets deleted.
        const isNotActive = specVenue.status === "inactive" || specVenue.status === "pending_review";
        if (isNotActive) {
          await api.put(`/venues/${venueId}`, { status: "active" });
        } else {
          await api.delete(`/venues/${venueId}`);
        }
        setSpecVenue((prevSpecVenue) => ({
          ...prevSpecVenue,
          status: isNotActive ? "active" : "inactive",
        }));
        setClickCount(0);
      } else {
        setClickCount((prevCount) => prevCount + 1);
      }
    } catch (err) {
      console.log(`error deleting users. this was the error: ${err}`);
    }
  };

  const handleVenueAcceptance = async () => {
    const requestId = searchParams.get("moderating");
    await api.put(`/venue-requests/accept/${requestId}`);
    setUrlSearch({});
    navigate("/moderators");
  };

  const handleVenueDeny = async () => {
    const requestId = searchParams.get("moderating");
    await api.put(`/venue-requests/deny/${requestId}`);
    setUrlSearch({});
    navigate("/moderators");
  };

  const [listStatusBusiness, setlistStatusBusiness] = useState();

  useEffect(() => {
    const getStatusBusinessRequest = async () => {
      try {
        const requestId = searchParams.get("moderating");
        if (!requestId) return;
        const response = await api.get(`/venue-requests/${requestId}`);
        setlistStatusBusiness(response.data);
      } catch (err) {
        console.log(
          `error retreiving specific venue. this was the error: ${err}`
        );
      }
    };
    getStatusBusinessRequest();
  }, [searchParams]);

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="edit-venue">
            {/* Top bar */}
            <div className="edit-venue__top">
              <div className="edit-venue__top-left">
                <img onClick={() => navigate(-1)} className="edit-venue__top-left-icon" src={chevronLeft} alt="back" style={{ cursor: 'pointer' }} />
                <h1 className="edit-venue__top-left-heading">Edit Business</h1>
              </div>
              <div className="edit-venue__top-button-wrapper">
                {listStatusBusiness?.status === "denied" && moderatingVenue && (
                  <button onClick={handleVenueAcceptance} className="venue-action-button accept">
                    Accept business request
                  </button>
                )}
                {listStatusBusiness?.status === "accepted" && moderatingVenue && (
                  <button onClick={handleVenueDeny} className="venue-action-button deny">
                    Deny business request
                  </button>
                )}
                {listStatusBusiness?.status === 'pending' && (
                  <>
                    <button onClick={handleVenueDeny} className="venue-action-button deny">
                      Deny business request
                    </button>
                    <button onClick={handleVenueAcceptance} className="venue-action-button accept">
                      Accept business request
                    </button>
                  </>
                )}
                <div className="venue-lifecycle">
                  {[
                    { key: 'open', label: 'Open' },
                    { key: 'hidden', label: 'Hidden' },
                    { key: 'closed', label: 'Closed' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={`venue-lifecycle__btn venue-lifecycle__btn--${key} ${venueLifecycle === key ? 'is-active' : ''}`}
                      onClick={() => setLifecycle(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Venue identity bar */}
            {specVenue.name && (
              <div className="edit-venue__identity">
                <img
                  className="edit-venue__identity-photo"
                  src={specVenue.image_urls?.[0] || ''}
                  alt={specVenue.name}
                />
                <div className="edit-venue__identity-info">
                  <h2 className="edit-venue__identity-name">{specVenue.name}</h2>
                  <p className="edit-venue__identity-address">{specVenue.address}</p>
                </div>
              </div>
            )}

            {(specVenue._id || specVenue.id) && (
              <>
                <VenueInviteLink venueId={venueId} venueName={specVenue.name} />
                <VenueTempLogin venueId={venueId} venueName={specVenue.name} />
                <VenueFeeFields venueId={venueId} />
                <VenueFloorPlan venueId={venueId} venueName={specVenue.name} />
              </>
            )}
            <VenueInformation />
          </section>
        </article>
      </div>
    </main>
  );
}

export default EditVenue;
